import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { authApi, User } from '../api';
import { isTokenExpired, getTokenTimeRemaining } from '../utils/jwt';
import { tokenStore, purgeLegacyTokenStorage } from '../utils/tokenStore';

// ── Constants ──────────────────────────────────────────────────────
const DEMO_FRONTEND_URL = process.env.REACT_APP_DEMO_FRONTEND_URL || '';
const TOKEN_REFRESH_BUFFER = 300; // Refresh token 5 minutes before expiry

// ── Context Types ──────────────────────────────────────────────────
interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isSuperuser: boolean;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ── Provider ───────────────────────────────────────────────────────
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const didRun = useRef(false);

  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Remove tokens persisted by older versions (issue #6).
      purgeLegacyTokenStorage();

      // Bootstrap via the orchestrator's httpOnly refresh cookie: the access
      // token is no longer passed through the URL or stored in localStorage.
      // A single silent refresh establishes the shared demo/admin session.
      if (!tokenStore.get()) {
        try {
          const res = await authApi.refreshOrchestratorToken();
          tokenStore.set(res.data?.access_token ?? null);
        } catch {
          // Not authenticated - let ProtectedRoute handle the redirect
          // Don't redirect here to avoid competing redirects
          setLoading(false);
          return;
        }
      }

      // Validate token
      const response = await authApi.whoami();
      setUser(response.data);
    } catch (err) {
      console.error('Auth check failed:', err);
      setError('Authentication failed');
      tokenStore.clear();
      // Don't redirect here - let ProtectedRoute handle it
      // This prevents competing redirect logic
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;
    checkAuth();
  }, [checkAuth]);

  // Proactively refresh the short-lived access token before it expires.
  // Access tokens are now 30-minute (issue #6); the httpOnly refresh cookie
  // keeps the session alive without user interaction.
  useEffect(() => {
    if (!user) return;

    let handle: number | undefined;

    const checkAndRefreshToken = async (): Promise<number | undefined> => {
      const token = tokenStore.get();
      if (!token) return undefined;

      if (isTokenExpired(token, TOKEN_REFRESH_BUFFER)) {
        try {
          const res = await authApi.refreshOrchestratorToken();
          const fresh = res.data?.access_token ?? null;
          if (!fresh) throw new Error('refresh response contained no access token');
          tokenStore.set(fresh);
          console.log('Access token silently refreshed.');
        } catch (err) {
          console.warn('Silent token refresh failed; clearing auth state.', err);
          tokenStore.clear();
          setUser(null);
          if (DEMO_FRONTEND_URL) {
            window.location.href = `${DEMO_FRONTEND_URL}/login`;
          }
          return undefined;
        }
      }

      const current = tokenStore.get() ?? token;
      const timeRemaining = getTokenTimeRemaining(current);
      const nextCheckIn = Math.min(60, Math.max(10, timeRemaining - TOKEN_REFRESH_BUFFER));
      return nextCheckIn * 1000;
    };

    const loop = async () => {
      const delay = await checkAndRefreshToken();
      if (delay === undefined) return;
      handle = window.setTimeout(loop, delay);
    };
    void loop();

    return () => {
      if (handle) clearTimeout(handle);
    };
  }, [user, location.pathname]);

  const logout = () => {
    tokenStore.clear();
    purgeLegacyTokenStorage();
    sessionStorage.removeItem('admin_redirect_in_progress');
    window.location.href = `${DEMO_FRONTEND_URL}/login`;
  };

  const value: AuthContextValue = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isSuperuser: user?.is_superuser === true || user?.role === 'superadmin',
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ── Hook ───────────────────────────────────────────────────────────
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
